import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { DashboardUser } from '@alertwhale/shared'

interface AuthState {
  user: DashboardUser | null
  token: string | null
  tenantId: string | null
  // Temp auth for 2FA flow
  tempToken: string | null
  tempUser: DashboardUser | null
  requires2FA: boolean
  setAuth: (user: DashboardUser, token: string, tenantId: string) => void
  setTempAuth: (tempToken: string, user: DashboardUser) => void
  clearTempAuth: () => void
  updateUser: (user: Partial<DashboardUser>) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      tenantId: null,
      tempToken: null,
      tempUser: null,
      requires2FA: false,
      setAuth: (user, token, tenantId) =>
        set({
          user,
          token,
          tenantId,
          tempToken: null,
          tempUser: null,
          requires2FA: false,
        }),
      setTempAuth: (tempToken, user) =>
        set({
          tempToken,
          tempUser: user,
          requires2FA: true,
        }),
      clearTempAuth: () =>
        set({
          tempToken: null,
          tempUser: null,
          requires2FA: false,
        }),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      logout: () =>
        set({
          user: null,
          token: null,
          tenantId: null,
          tempToken: null,
          tempUser: null,
          requires2FA: false,
        }),
    }),
    {
      name: 'alertwhale-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        tenantId: state.tenantId,
        // Don't persist temp auth - it's only for the current session
      }),
    }
  )
)
